import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appDate } from '@/utils/appDateFormat';
import { ShieldCheck } from 'lucide-react';
import Switch from '../../../components/ui/Switch';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Spinner } from '../../../components/ui/Spinner';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import {
  CompanyMfaPolicy,
  CompanyMfaPolicyInput,
  MfaRole,
  getCompanyMfaPolicy,
  updateCompanyMfaPolicy,
} from '../../../api/General/Mfa';

/**
 * CO-323 — per-company multi-factor authentication policy (Operator only).
 *
 * Owner decisions 2026-09-15: off for every existing tenant until an Operator turns it on here;
 * roles, grace period and remember-device window are all configurable per company. Saved on its
 * own (PUT api/Mfa/company/{id}/policy), independent of the company details form above it.
 */
interface Props {
  companyId: number;
}

const ROLE_ORDER: MfaRole[] = ['Operator', 'Hr', 'Admin', 'Chief', 'Manager', 'Clocking'];

/** Server DateTimes read back from EF carry no zone; they are UTC. */
const parseUtc = (value?: string | null): Date | null => {
  if (!value) return null;
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

export default function OperatorMfaPolicyPanel({ companyId }: Props) {
  const { t, i18n } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [policy, setPolicy] = useState<CompanyMfaPolicy | null>(null);
  const [draft, setDraft] = useState<CompanyMfaPolicyInput | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCompanyMfaPolicy(companyId);
      if (res.errors?.length) {
        showToast(getApiErrorMessage(res, t, t('mfa.policy.loadFailed')), 'error');
        return;
      }
      setPolicy(res.payload);
      setDraft({
        isEnforced: res.payload.isEnforced,
        enforcedRoles: res.payload.enforcedRoles,
        gracePeriodDays: res.payload.gracePeriodDays,
        rememberDeviceDays: res.payload.rememberDeviceDays,
      });
    } catch (e) {
      showToast(getApiErrorMessage(e, t, t('mfa.policy.loadFailed')), 'error');
    } finally {
      setLoading(false);
    }
  }, [companyId, showToast, t]);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = useCallback(
    (value?: string | null) => appDate(parseUtc(value)),
    [i18n.language],
  );

  const graceError = useMemo(() => {
    if (!draft || !policy) return undefined;
    const v = draft.gracePeriodDays;
    return Number.isInteger(v) && v >= 0 && v <= policy.maxGracePeriodDays
      ? undefined
      : t('mfa.policy.rangeError', { max: policy.maxGracePeriodDays });
  }, [draft, policy, t]);

  const rememberError = useMemo(() => {
    if (!draft || !policy) return undefined;
    const v = draft.rememberDeviceDays;
    return Number.isInteger(v) && v >= 0 && v <= policy.maxRememberDeviceDays
      ? undefined
      : t('mfa.policy.rangeError', { max: policy.maxRememberDeviceDays });
  }, [draft, policy, t]);

  const rolesError = draft?.isEnforced && draft.enforcedRoles.length === 0 ? t('mfa.policy.noRoles') : undefined;
  const invalid = !!(graceError || rememberError || rolesError);

  const toggleRole = (role: MfaRole, checked: boolean) =>
    setDraft((d) => {
      if (!d) return d;
      const next = checked ? [...d.enforcedRoles, role] : d.enforcedRoles.filter((r) => r !== role);
      return { ...d, enforcedRoles: ROLE_ORDER.filter((r) => next.includes(r)) };
    });

  const save = async () => {
    if (!draft || invalid) return;
    setSaving(true);
    try {
      const res = await updateCompanyMfaPolicy(companyId, draft);
      if (res.errors?.length) {
        showToast(getApiErrorMessage(res, t, t('mfa.policy.saveFailed')), 'error');
        return;
      }
      setPolicy(res.payload);
      setDraft({
        isEnforced: res.payload.isEnforced,
        enforcedRoles: res.payload.enforcedRoles,
        gracePeriodDays: res.payload.gracePeriodDays,
        rememberDeviceDays: res.payload.rememberDeviceDays,
      });
      showToast(t('mfa.policy.saved'), 'success');
    } catch (e) {
      showToast(getApiErrorMessage(e, t, t('mfa.policy.saveFailed')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const numberValue = (raw: string) => (raw === '' ? Number.NaN : Number(raw));
  const availableRoles = ROLE_ORDER.filter((r) => policy?.availableRoles?.includes(r) ?? true);

  return (
    <section
      className="flex w-full flex-col gap-4 rounded-lg border border-border bg-card p-4 sm:p-5"
      aria-labelledby="mfa-policy-title"
      data-testid="mfa-policy-panel"
    >
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <div className="flex flex-col gap-1">
          <h2 id="mfa-policy-title" className="text-base font-semibold text-foreground">
            {t('mfa.policy.title')}
          </h2>
          <p className="text-sm text-muted-foreground">{t('mfa.policy.description')}</p>
        </div>
      </div>

      {loading || !draft || !policy ? (
        <div className="flex justify-center py-6">
          <Spinner size="md" />
        </div>
      ) : (
        <>
          {!policy.platformEnabled && (
            <p role="status" className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              {t('mfa.policy.platformDisabled')}
            </p>
          )}

          <Switch
            checked={draft.isEnforced}
            onCheckedChange={(checked) => setDraft((d) => (d ? { ...d, isEnforced: checked } : d))}
            label={t('mfa.policy.enforce')}
            description={t('mfa.policy.enforceHint')}
            data-testid="mfa-policy-enforce"
          />

          <p className="text-sm text-muted-foreground" role="status" data-testid="mfa-policy-status">
            {policy.isEnforced
              ? t('mfa.policy.statusOn', {
                  since: formatDate(policy.enforcementEnabledAtUtc),
                  graceEnds: formatDate(policy.graceEndsAtUtc),
                })
              : t('mfa.policy.statusOff')}
          </p>

          <fieldset className="flex flex-col gap-2" disabled={!draft.isEnforced}>
            <legend className="mb-1 text-sm font-medium text-foreground">{t('mfa.policy.roles')}</legend>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
              {availableRoles.map((role) => (
                <Checkbox
                  key={role}
                  label={t(`mfa.policy.role.${role}`)}
                  checked={draft.enforcedRoles.includes(role)}
                  disabled={!draft.isEnforced}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => toggleRole(role, e.target.checked)}
                  data-testid={`mfa-policy-role-${role}`}
                />
              ))}
            </div>
            {rolesError && (
              <p className="text-sm text-destructive" role="alert">
                {rolesError}
              </p>
            )}
          </fieldset>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              max={policy.maxGracePeriodDays}
              step={1}
              label={`${t('mfa.policy.grace')} (${t('mfa.policy.days')})`}
              description={t('mfa.policy.graceHint', { max: policy.maxGracePeriodDays })}
              error={graceError}
              value={Number.isNaN(draft.gracePeriodDays) ? '' : draft.gracePeriodDays}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((d) => (d ? { ...d, gracePeriodDays: numberValue(e.target.value) } : d))
              }
              data-testid="mfa-policy-grace"
            />
            <Input
              type="number"
              inputMode="numeric"
              min={0}
              max={policy.maxRememberDeviceDays}
              step={1}
              label={`${t('mfa.policy.remember')} (${t('mfa.policy.days')})`}
              description={t('mfa.policy.rememberHint', { max: policy.maxRememberDeviceDays })}
              error={rememberError}
              value={Number.isNaN(draft.rememberDeviceDays) ? '' : draft.rememberDeviceDays}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDraft((d) => (d ? { ...d, rememberDeviceDays: numberValue(e.target.value) } : d))
              }
              data-testid="mfa-policy-remember"
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={saving ? undefined : save} disabled={saving || invalid} loading={saving} data-testid="mfa-policy-save">
              {t('mfa.policy.save')}
            </Button>
          </div>
        </>
      )}

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </section>
  );
}
